import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import moment from "moment";

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {Object} config - Configuration object
 * @param {string} config.filename - Base filename (without extension)
 * @param {string} config.sheetName - Name of the Excel sheet
 * @param {Object} config.columnMapping - Object mapping data keys to display headers
 * @param {boolean} config.autoFitColumns - Whether to auto-fit column widths
 * @param {boolean} config.showToast - Whether to show success/error toast messages
 * @param {number} config.maxColumnWidth - Maximum column width
 * @returns {boolean} - Success status
 */
export const exportToExcel = (data, config = {}) => {
    const {
        filename = 'export',
        sheetName = 'Sheet1',
        columnMapping = null,
        autoFitColumns = true,
        showToast = true,
        maxColumnWidth = 50
    } = config;

    try {
        if (!data || !Array.isArray(data) || data.length === 0) {
            if (showToast) toast.warning("No data available to export");
            return false;
        }

        // Prepare data for Excel export
        let excelData;
        if (columnMapping) {
            excelData = data.map(row => {
                const mappedRow = {};
                Object.keys(columnMapping).forEach(key => {
                    mappedRow[columnMapping[key]] = row[key] || '';
                });
                return mappedRow;
            });
        } else {
            excelData = data;
        }

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        
        // Auto-fit column widths if enabled
        if (autoFitColumns && ws['!ref']) {
            const range = XLSX.utils.decode_range(ws['!ref']);
            const wscols = [];
            for (let C = range.s.c; C <= range.e.c; ++C) {
                let maxWidth = 10;
                for (let R = range.s.r; R <= range.e.r; ++R) {
                    const cell = ws[XLSX.utils.encode_cell({r: R, c: C})];
                    if (cell && cell.v) {
                        const width = cell.v.toString().length;
                        if (width > maxWidth) maxWidth = width;
                    }
                }
                wscols.push({ wch: Math.min(maxWidth + 2, maxColumnWidth) });
            }
            ws['!cols'] = wscols;
        }
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Generate filename with timestamp
        const timestamp = moment().format('lll').replace(/:/g, '-');
        const finalFilename = `${filename}_${timestamp}.xlsx`;

        // Export file
        XLSX.writeFile(wb, finalFilename);
        
        if (showToast) toast.success(`Excel export completed successfully! File: ${finalFilename}`);
        return true;
        
    } catch (error) {
        console.error("Excel export error:", error);
        if (showToast) toast.error(`Failed to export Excel file: ${error.message}`);
        return false;
    }
};

/**
 * Export data to Excel with multiple sheets
 * @param {Array} sheetsData - Array of sheet objects [{data, name, columnMapping}]
 * @param {string} filename - Base filename (without extension)
 * @param {Object} options - Additional options
 * @returns {boolean} - Success status
 */
export const exportMultiSheetExcel = (sheetsData, filename = 'multi_sheet_export', options = {}) => {
    const { showToast = true, autoFitColumns = true, maxColumnWidth = 50 } = options;

    try {
        if (!sheetsData || !Array.isArray(sheetsData) || sheetsData.length === 0) {
            if (showToast) toast.warning("No sheets data available to export");
            return false;
        }

        // Create workbook
        const wb = XLSX.utils.book_new();

        sheetsData.forEach((sheet, index) => {
            const {
                data,
                name = `Sheet${index + 1}`,
                columnMapping = null
            } = sheet;

            if (!data || data.length === 0) return;

            // Prepare data for this sheet
            let sheetData;
            if (columnMapping) {
                sheetData = data.map(row => {
                    const mappedRow = {};
                    Object.keys(columnMapping).forEach(key => {
                        mappedRow[columnMapping[key]] = row[key] || '';
                    });
                    return mappedRow;
                });
            } else {
                sheetData = data;
            }

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(sheetData);

            // Auto-fit columns if enabled
            if (autoFitColumns && ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                const wscols = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxWidth = 10;
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = ws[XLSX.utils.encode_cell({r: R, c: C})];
                        if (cell && cell.v) {
                            const width = cell.v.toString().length;
                            if (width > maxWidth) maxWidth = width;
                        }
                    }
                    wscols.push({ wch: Math.min(maxWidth + 2, maxColumnWidth) });
                }
                ws['!cols'] = wscols;
            }

            // Add sheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, name);
        });

        // Generate filename with timestamp
        const timestamp = moment().format('lll').replace(/:/g, '-');
        const finalFilename = `${filename}_${timestamp}.xlsx`;

        // Export file
        XLSX.writeFile(wb, finalFilename);
        
        if (showToast) toast.success(`Multi-sheet Excel export completed successfully! File: ${finalFilename}`);
        return true;
        
    } catch (error) {
        console.error("Multi-sheet Excel export error:", error);
        if (showToast) toast.error(`Failed to export multi-sheet Excel file: ${error.message}`);
        return false;
    }
};

/**
 * Export authentication history data to Excel
 * @param {Array} data - Authentication history data array
 * @param {Object} columnMapping - Column mapping for the export
 * @param {Object} customConfig - Custom configuration to override defaults
 * @returns {boolean} - Success status
 */
export const exportAuthHistoryToExcel = (data, columnMapping, customConfig = {}) => {
    const defaultConfig = {
        filename: 'authentication_history_export',
        sheetName: 'Authentication History',
        columnMapping,
        // columnMapping: {
        //     'acsTransId': 'ACS Transaction ID',
        //     'dsTransId': 'DS Transaction ID',
        //     'merchantName': 'Merchant Name',
        //     'cardNumber': 'Card Number',
        //     'transactionType': 'Transaction Type',
        //     'status': 'Status',
        //     'institution': 'Institution',
        //     'schemeName': 'Scheme Name',
        //     'transactionDateTime': 'Transaction Date Time'
        // },
        autoFitColumns: true,
        showToast: true,
        maxColumnWidth: 30
    };

    const config = { ...defaultConfig, ...customConfig };
    return exportToExcel(data, config);
};

export const exportReportToExcel = (data, columnMapping, customConfig = {}) => {
    const defaultConfig = {
        filename: data?.name || 'report_export',
        sheetName: data?.name || 'Report',
        columnMapping,
        autoFitColumns: true,
        showToast: true,
        // maxColumnWidth: 30
    };

    const config = { ...defaultConfig, ...customConfig };
    return exportToExcel(data?.list, config);
};
